/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Row, Col, Spin, Card } from 'antd';
import { connect } from 'dva';
import moment from 'moment';
import DOMPurify from 'dompurify';


const createCompanyHubspotTaskUrl = (hubspotPortalId, companyId, taskId) => {
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-2/${companyId}?taskId=${taskId}`;
};

const createDealHubspotTaskUrl = (hubspotPortalId, dealId, taskId) => {
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-3/${dealId}?taskId=${taskId}`;
};

const createHubspotDealUrl = (hubspotPortalId, dealId) => {
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/deal/${dealId}`;
};

const renderTaskBody = (body) => {
  if (!body) {
    return null;
  }

  const regex = /(https?:\/\/[^\s]+)/g;
  const replacedLinks = body.replace(regex, '<a rel="noopener noreferrer" target="_blank" href="$1">$1</a>');

  return DOMPurify.sanitize(replacedLinks, { ALLOWED_TAGS: ['a'] });
};

function ViewHubspotTransfer(props) {
  const [hubspotTransferId, setHubspotTransferId] = useState(props.hubspotTransferId);
  const currentProject = props.app.currentProject;

  useEffect(() => {
    setHubspotTransferId(props.hubspotTransferId);
    props.dispatch({
      type: 'hubspot/loadHubspotTransfer',
      payload: {
        projectId: currentProject.id,
        hubspotTransferId,
      },
    }).then((transfer) => {
      if (!transfer) {
        props.onTaskNotFound();
      }
    });
  }, []);

  const hubspotTransfer = props.hubspot.currentTaskView.created_at ? props.hubspot.currentTaskView : {
    hs_task_id: null,
    hs_company_url: null,
    company_name: null,
    hs_deal_id: null,
    hs_company_id: null,
    hs_task_subject: null,
    hs_task_body: null,
    hs_timestamp: null,
    created_by: { id: null, label: null },
    company: { id: null, label: null },
    hs_owner: { id: null, label: null },
    hs_deal_pipeline: { id: null, label: null },
    hs_deal_stage: { id: null, label: null },
    hs_deal_category: { id: null, label: null },
    hs_deal_type: { id: null, label: null },
    hs_deal_cluster: { id: null, label: null },
    hs_deal_priority: null,
    hs_task_priority: null,
    created_at: null,
  };

  const hubspotTaskUrl = hubspotTransfer.hs_deal_id ?
    createDealHubspotTaskUrl(currentProject.hubspot_portal_id, hubspotTransfer.hs_deal_id, hubspotTransfer.hs_task_id) :
    createCompanyHubspotTaskUrl(currentProject.hubspot_portal_id, hubspotTransfer.hs_company_id, hubspotTransfer.hs_task_id);

  const hubspotDealUrl = createHubspotDealUrl(currentProject.hubspot_portal_id, hubspotTransfer.hs_deal_id);

  const displayTask = hubspotTransfer.hs_task_id ? 'block' : 'none';
  const displayDeal = hubspotTransfer.hs_deal_id ? 'block' : 'none';

  const dealPriority = hubspotTransfer.hs_deal_priority ? hubspotTransfer.hs_deal_priority.label : null;
  const taskPriority = hubspotTransfer.hs_task_priority ? hubspotTransfer.hs_task_priority.label : null;

  return (
    <Card
      className="card-multiple"
    >
      <Spin
        spinning={
          !!props.loading.effects['hubspot/loadHubspotTransfer']
        }
      >
        <Row type="flex" gutter={{ xs: 8, sm: 16, md: 24 }}>
          <Col xxl={6} xl={8} lg={10} md={24} sm={24} xs={24} style={{ marginTop: '10px' }}>
            <a rel="noopener noreferrer" target="_blank" href={hubspotTransfer.hs_company_url}>
              <h3>{hubspotTransfer.company_name}</h3>
            </a>
          </Col>

          {/* assignee */}
          <Col xxl={6} xl={8} lg={10} md={24} sm={24} xs={24}>
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.AssignedTo" />
            </h5>
            <p>
              {hubspotTransfer.hs_owner.label}<br />
              {moment(hubspotTransfer.created_at).format(props.app.user.display_datetime_format)}<br />
              <FormattedMessage
                id="Component.CreateHubspotTask.View.AssignedBy"
                values={{ fullname: hubspotTransfer.created_by.label }}
              />
            </p>
          </Col>

          {/* due date */}
          <Col
            style={{ display: displayTask }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.DueDate" />
            </h5>
            <p>
              {moment(hubspotTransfer.hs_timestamp).format(props.app.user.display_datetime_format)}
            </p>
          </Col>

          {/* Deal Pipeline */}
          <Col
            style={{ display: displayDeal }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.DealPipeline" />
            </h5>
            <p>
              {hubspotTransfer.hs_deal_pipeline ? hubspotTransfer.hs_deal_pipeline.label : null}<br />
            </p>
          </Col>

          {/* Deal Stage */}
          <Col
            style={{ display: displayDeal }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.DealStage" />
            </h5>
            <p>
              {hubspotTransfer.hs_deal_stage ? hubspotTransfer.hs_deal_stage.label : null}<br />
            </p>
          </Col>

          {/* Deal Cluster */}
          <Col
            style={{ display: displayDeal }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.DealCluster" />
            </h5>
            <p>
              {hubspotTransfer.hs_deal_cluster ?
                hubspotTransfer.hs_deal_cluster.label :
                props.intl.formatMessage({ id: 'Component.CreateHubspotTask.View.DealValueNone' })
              }<br />
            </p>
          </Col>

          {/* Deal Topic */}
          <Col
            style={{ display: displayDeal }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.DealTopic" />
            </h5>
            <p>
              {hubspotTransfer.hs_deal_category ?
                hubspotTransfer.hs_deal_category.label :
                props.intl.formatMessage({ id: 'Component.CreateHubspotTask.View.DealValueNone' })
              }<br />
            </p>
          </Col>

          {/* Deal Type */}
          <Col
            style={{ display: displayDeal }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.DealType" />
            </h5>
            <p>
              {hubspotTransfer.hs_deal_type ?
                hubspotTransfer.hs_deal_type.label :
                props.intl.formatMessage({ id: 'Component.CreateHubspotTask.View.DealValueNone' })
              }<br />
            </p>
          </Col>

          {/* Priority */}
          <Col xxl={6} xl={8} lg={10} md={24} sm={24} xs={24}>
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.Priority" />
            </h5>
            <p>
              {
                dealPriority ||
                taskPriority ||
                <FormattedMessage id="Component.CreateHubspotTask.View.PriorityValueNone" />
              }
            </p>
          </Col>

          {/* Notes */}
          <Col
            style={{ display: displayTask }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.Notes" />
            </h5>
            <p
              style={{ whiteSpace: 'pre-line' }}
              dangerouslySetInnerHTML={{ __html: renderTaskBody(hubspotTransfer.hs_task_body) }}
            />
          </Col>
          <Col
            style={{ display: displayTask }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.HubspotUrl" />
            </h5>
            <p>
              <a rel="noopener noreferrer" target="_blank" href={hubspotTaskUrl}>{hubspotTaskUrl}</a>
            </p>
          </Col>

          <Col
            style={{ display: displayDeal }}
            xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}
          >
            <h5>
              <FormattedMessage id="Component.CreateHubspotTask.View.HubspotDealUrl" />
            </h5>
            <p>
              <a rel="noopener noreferrer" target="_blank" href={hubspotDealUrl}>{hubspotDealUrl}</a>
            </p>
          </Col>
        </Row>
      </Spin>
    </Card>
  );
}

ViewHubspotTransfer.propTypes = {
  hubspotTransferId: PropTypes.number.isRequired,
  onTaskNotFound: PropTypes.func.isRequired,
};


export default connect(({ app, loading, hubspot }) =>
  ({ app, loading, hubspot }))((injectIntl(ViewHubspotTransfer)));



// WEBPACK FOOTER //
// ./src/components/Leads/Hubspot/ViewHubspotTransfer/index.js